/** Profile state store using Zustand. */

import { create } from 'zustand'
import * as api from '../api/client'
import { config } from '../config'
import type { ProfileSummary } from '../types/profile'

interface ProfileState {
  profiles: ProfileSummary[]
  activeProfileId: string | null
  activeProfileName: string | null
  setupComplete: boolean
  loading: boolean
  error: string | null

  fetchProfiles: () => Promise<void>
  checkSetupStatus: () => Promise<boolean>
  activateProfile: (id: string) => Promise<void>
  reset: () => void
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  profiles: [],
  activeProfileId: null,
  activeProfileName: null,
  setupComplete: false,
  loading: false,
  error: null,

  fetchProfiles: async () => {
    set({ loading: true, error: null })
    try {
      const data = await api.get<{ profiles: ProfileSummary[] }>('/config/profiles')
      const active = data.profiles.find((p) => p.isActive) ?? null
      set({
        profiles: data.profiles,
        activeProfileId: active?.id ?? null,
        activeProfileName: active?.name ?? null,
        setupComplete: data.profiles.some((p) => p.setupComplete),
        loading: false,
      })
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to load profiles',
      })
    }
  },

  checkSetupStatus: async () => {
    if (config.useMocks) {
      const completed = localStorage.getItem('aegis_setup_complete') === 'true'
      set({ setupComplete: completed })
      return completed
    }
    try {
      const data = await api.get<{ completed: boolean }>('/config/setup-status')
      set({ setupComplete: data.completed })
      return data.completed
    } catch {
      return false
    }
  },

  activateProfile: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await api.post(`/config/profiles/${id}/activate`)
      await get().fetchProfiles()
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to activate profile',
      })
    }
  },

  reset: () => {
    set({
      profiles: [],
      activeProfileId: null,
      activeProfileName: null,
      setupComplete: false,
      loading: false,
      error: null,
    })
  },
}))
